/**
 * Image Component
 */

import React, { SyntheticEvent, FC, useState } from 'react';
import { ImageProps } from './image.types';
import { ImageWrapper, Img, Placeholder } from './image.style';
import LazyLoad from 'react-lazyload';
import FixedAspect from '../utilComponents/FixedAspect';
import bindHandlers from '../../utils/handlerHelper';
import Handler from '../../commonTypes/handler.types';
import { SkeletonColors } from '../Skeleton/skeleton.types';


const SPACE: string = ' ';

// Construct a srcset string if passed an object
const getSrcsetString: Function = (srcset: object | string): string => {
  if (typeof srcset === 'string') {
    return srcset;
  }
  const arr: Array<string> = [];
  for (let s in srcset) {
    arr.push(srcset[s] + SPACE + s);
  }
  return arr.join(', ');
};

// Made the Image component a class to make use of state for
// error image handing
const Image: FC<ImageProps> = ({
  lazyload = false,
  srcset,
  sizes,
  src,
  rounded = false,
  width = '100%',
  height,
  alt,
  title,
  lazyloadOffset = 200,
  placeHolderHeight,
  placeHolderWidth,
  errorImageSrc,
  fixAspect = false,
  ratio,
  handlers = {},
  backgroundColor,
  className,
  border,
  padding = '0px',
  borderRadius,
  theme = {},
  dataAttr
}: ImageProps) => {
  const [visible, setVisible] = useState<boolean>(true);
  const [srcSetDisabled, setSrcSetDisabled] = useState<boolean>(false);
  const [errSrc, setErrorImageSrc] = useState<string | undefined>(undefined);

  // Handle the image error.
  // If there is a handler, call it.
  // If there is a provided error image src, use it
  // Else if we have a fallback image, use it
  // If nothing else, set to invisible
  const handleImageError = (e: SyntheticEvent<HTMLImageElement>) => {
    if (errorImageSrc) {
      setSrcSetDisabled(true);
      setErrorImageSrc(errorImageSrc);
    } else {
      setVisible(false);
    }
  };

  // Establish the src and srcset strings
  const { dark = false } = theme;

  const srcString: string = errSrc || src;
  const srcsetString: string | undefined = (srcset && !srcSetDisabled) ? getSrcsetString(srcset) : undefined;

  // Set up the handlers
  const imageHandlers: Handler = {
    onError: (e: SyntheticEvent<HTMLImageElement>) => { handleImageError(e); }
  };
  const combinedHandlers: Handler = bindHandlers(null, handlers, imageHandlers);


  // Set up the image itself
  let image = (
    <Img
      key={src}
      src={ srcString }
      srcSet={ srcsetString }
      sizes={ sizes }
      alt={ alt }
      title={ title }
      width={ fixAspect ? '100%' : width }
      height={height}
      backgroundColor={backgroundColor}
      padding={padding}
      rounded={rounded}
      borderRadius={borderRadius}
      { ...combinedHandlers }
      {...dataAttr}
    />
  );

  // Fix the image aspect ratio if flag is passed
  if (fixAspect) {
    image = (
      <FixedAspect ratio={ ratio }>
        { image }
      </FixedAspect>
    );
  }

  if (lazyload) {

    const placeholderBgColor = (dark) ? SkeletonColors.dark.main : SkeletonColors.primary.main;
    const placeholderComp = (<Placeholder
      h={height}
      w={width}
      placeholderWidth={placeHolderWidth}
      placeholderHeight={placeHolderHeight}
      backgroundColor={placeholderBgColor}
      border={border}
      ratio={ratio}
    />);

    image = (
      <LazyLoad placeholder={ placeholderComp } once offset={ [lazyloadOffset, 0] }>
        { image }
      </LazyLoad>
    );
  }

  return (
    <ImageWrapper
      className={className}
      rounded={rounded}
      visible={visible}
      w={width}
      h={height}
      border={border}
      borderRadius={borderRadius}
    >
      { image }
    </ImageWrapper>
  );
};

export default Image;
